import { useState, useEffect, useCallback } from 'react';

interface GalleryImage {
  id: number;
  src: string;
  title: string;
  category: string;
  description: string;
}

const galleryImages: GalleryImage[] = [
  {
    id: 1,
    src: 'https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/1d3b9ccf43e603226e103140e0cc6884.jpeg',
    title: 'Chrome Ore Drilling Operations',
    category: 'Mining',
    description:
      'Active drilling operations at one of our chrome ore extraction sites in the Northwest Province.',
  },
  {
    id: 2,
    src: 'https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/f99639ebc7bfcad8022a6b3e88df7cea.jpeg',
    title: 'Chrome Processing Plant',
    category: 'Processing',
    description: 'Our Rustenburg-based chrome ore beneficiation and processing facility.',
  },
  {
    id: 3,
    src: 'https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/29f821809ccae55c0add2340fb43077a.jpeg',
    title: 'TMI Logistics Fleet',
    category: 'Logistics',
    description: 'Our dedicated transport fleet ensuring reliable delivery of chrome products.',
  },
  {
    id: 4,
    src: 'https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/03cae614040a929a65775a2758c29218.jpeg',
    title: 'Quality Control & Safety',
    category: 'Operations',
    description:
      'Commitment to quality assurance and safety standards across all operations.',
  },
  {
    id: 5,
    src: 'https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/fe8bc750681395e13b124b6ad44e0ad7.jpeg',
    title: 'Tlotlo Mfolwe Incorporated',
    category: 'Corporate',
    description: 'TMI — your trusted chrome commodity trading and processing partner.',
  },
  {
    id: 18,
    src: 'https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/ec1501edbc44d32bc1eafc111fb3ab12.jpeg',
    title: 'Mining Operations Team',
    category: 'Operations',
    description:
      'TMI operations team at the mining site with heavy machinery, demonstrating our commitment to safety and professionalism.',
  },
  {
    id: 19,
    src: 'https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/e1d41af94e29aedf3091ffdd26f07f07.jpeg',
    title: 'Crew at the Crusher',
    category: 'Operations',
    description:
      'TMI crew in full safety gear standing alongside the Pegson mobile crusher, showcasing teamwork and operational readiness on site.',
  },
  {
    id: 20,
    src: 'https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/5af1424d9198d24f1373dacf84c707f2.jpeg',
    title: 'Vertical Shaft Impact Crusher',
    category: 'Processing',
    description:
      'Heavy-duty VSI crusher installed at TMI\'s processing facility, used for fine crushing and shaping of chrome ore material.',
  },
  {
    id: 21,
    src: 'https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/82f2c7cdb30250df162db38a009014d8.jpeg',
    title: 'Spiral Concentrator Plant',
    category: 'Processing',
    description:
      'TMI\'s spiral concentrator plant in operation, separating chrome ore by gravity to produce high-grade concentrate output.',
  },
  {
    id: 22,
    src: 'https://static.readdy.ai/image/3b8ad55d4ff58d61aa412f5935e39846/f8cd5fd7e452f246cd93437aa5ed3bd1.jpeg',
    title: 'Chrome Ore Hand Sample',
    category: 'Mining',
    description:
      'Close-up of raw chrome ore material held in hand, showcasing the texture and quality of TMI\'s mined chromite.',
  },
];

const categories = ['All', 'Mining', 'Processing', 'Logistics', 'Operations', 'Corporate'];

export default function GalleryGrid() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const filtered =
    activeCategory === 'All'
      ? galleryImages
      : galleryImages.filter((img) => img.category === activeCategory);

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % filtered.length);
  }, [filtered.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
  }, [filtered.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [lightboxOpen, goNext, goPrev]);

  const getSpanClass = (index: number) => {
    // Create a visually interesting layout pattern
    const pattern = index % 6;
    if (pattern === 0) return 'md:col-span-2 md:row-span-2';
    if (pattern === 5) return 'md:col-span-2 md:row-span-1';
    return 'md:col-span-1 md:row-span-1';
  };

  return (
    <section className="py-20 bg-[#FAFAFA]">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Category Filter */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-14">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer font-['Lato'] ${
                activeCategory === cat
                  ? 'bg-[#8B1538] text-white shadow-lg shadow-[#8B1538]/25'
                  : 'bg-white text-gray-600 hover:bg-[#8B1538]/10 hover:text-[#8B1538] border border-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Image Count */}
        <div className="text-center mb-8">
          <p className="text-gray-500 text-sm font-['Lato']">
            Showing {filtered.length} {filtered.length === 1 ? 'image' : 'images'}
            {activeCategory !== 'All' && ` in ${activeCategory}`}
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 auto-rows-[280px]">
          {filtered.map((image, index) => (
            <div
              key={image.id}
              onClick={() => openLightbox(index)}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer shadow-md hover:shadow-2xl transition-all duration-500 ${getSpanClass(
                index,
              )}`}
            >
              <img
                src={image.src}
                alt={image.title}
                title={`${image.title} - TMI Chrome Operations`}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Category Badge */}
              <div className="absolute top-4 right-4 bg-[#8B1538]/90 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0 font-['Lato']">
                {image.category}
              </div>

              {/* Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                <h3 className="text-white text-xl font-bold mb-1 font-['Montserrat']">{image.title}</h3>
                <p className="text-white/75 text-sm font-['Lato'] leading-relaxed">{image.description}</p>
              </div>

              {/* Zoom Icon */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30">
                  <i className="ri-zoom-in-line text-white text-2xl"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-image-line text-gray-400 text-3xl"></i>
            </div>
            <p className="text-gray-500 text-lg font-['Lato']">No images found in this category.</p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && filtered.length > 0 && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-white text-2xl"></i>
          </button>

          {/* Counter */}
          <div className="absolute top-6 left-6 text-white/60 text-sm font-['Lato']">
            {currentIndex + 1} / {filtered.length}
          </div>

          {/* Previous */}
          {filtered.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              className="absolute left-4 md:left-8 w-14 h-14 flex items-center justify-center bg-white/10 hover:bg-[#8B1538]/80 rounded-full transition-all cursor-pointer z-10"
            >
              <i className="ri-arrow-left-s-line text-white text-3xl"></i>
            </button>
          )}

          {/* Image */}
          <div className="max-w-[90vw] max-h-[80vh] relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={filtered[currentIndex].src}
              alt={filtered[currentIndex].title}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
          </div>

          {/* Next */}
          {filtered.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              className="absolute right-4 md:right-8 w-14 h-14 flex items-center justify-center bg-white/10 hover:bg-[#8B1538]/80 rounded-full transition-all cursor-pointer z-10"
            >
              <i className="ri-arrow-right-s-line text-white text-3xl"></i>
            </button>
          )}

          {/* Info Bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-8 py-8">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block bg-[#8B1538]/80 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 font-['Lato']">
                {filtered[currentIndex].category}
              </span>
              <h3 className="text-white text-2xl font-bold font-['Montserrat'] mb-2">
                {filtered[currentIndex].title}
              </h3>
              <p className="text-white/70 text-sm font-['Lato']">{filtered[currentIndex].description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
